import React, { useEffect, useRef, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import {
    MousePointer2, Eye, Sparkles, Monitor, Apple, Check, RotateCcw,
    Palette, Ruler, Zap, Hand, Coffee, Mail, ShieldCheck,
    Crown, Share2, Search, Lock, KeyRound,
} from "lucide-react";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ThanksPage from "./pages/ThanksPage";
import PrintGuidePage from "./pages/PrintGuidePage";
import { PaymentSuccessPage, PaymentCancelPage } from "./pages/PaymentPages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ------------------------------------------------------------------ */
/* Cursor palette                                                      */
/* ------------------------------------------------------------------ */
const COLORS = [
    { id: "orange", label: "Bright Orange", hex: "#FF5722", stroke: "#7c2d12" },
    { id: "green",  label: "Bright Green",  hex: "#00E676", stroke: "#064e3b" },
    { id: "pink",   label: "Hot Pink",      hex: "#EC407A", stroke: "#831843" },
    { id: "yellow", label: "Sunny Yellow",  hex: "#FFD600", stroke: "#713f12" },
    { id: "cyan",   label: "Sky Cyan",      hex: "#00BCD4", stroke: "#164e63" },
    { id: "purple", label: "Royal Purple",  hex: "#8E24AA", stroke: "#4a1d5e" },
];

const TIP_TIERS = [
    { key: "tip_small",  label: "Small tip",  amount: "$3",  hint: "A high-five" },
    { key: "tip_medium", label: "Coffee",     amount: "$5",  hint: "Most loved 🧡" },
    { key: "tip_large",  label: "Big thanks", amount: "$10", hint: "You're a legend" },
];

const SHAPES = [
    { id: "arrow",     label: "Classic arrow", pro: false },
    { id: "hand",      label: "Pointing hand", pro: true },
    { id: "dot",       label: "Big dot",       pro: true },
    { id: "crosshair", label: "Crosshair",     pro: true },
    { id: "target",    label: "Target ring",   pro: true },
    { id: "heart",     label: "Heart",         pro: true },
    { id: "star",      label: "Star",          pro: true },
    { id: "pumpkin",   label: "Pumpkin",       pro: true },
    { id: "snowflake", label: "Snowflake",     pro: true },
    { id: "cake",      label: "Birthday cake", pro: true },
    { id: "paw",       label: "Paw print",     pro: true },
];

const DEFAULTS = {
    colorId: "orange",
    size: 34,
    trail: true,
    clickRing: true,
    shape: "arrow",
};

/* ------------------------------------------------------------------ */
/* Cursor shapes                                                       */
/* ------------------------------------------------------------------ */
const CursorShape = ({ shape = "arrow", size, fill, stroke }) => {
    const common = {
        width: size,
        height: size,
        viewBox: "0 0 24 24",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        style: { display: "block", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.35))" },
    };
    const sw = 1.4;
    if (shape === "hand") {
        return (
            <svg {...common}>
                <path
                    d="M9 3 v10 M9 8 c0-1 1-2 2-2 s2 1 2 2 v3 M13 9 c0-1 1-2 2-2 s2 1 2 2 v3 M17 10 c0-1 1-2 2-2 s2 1 2 2 v5 c0 3-2 6-5 6 h-5 c-3 0-5-2-6-4 l-3-6 c-.5-1 0-2 1-2.5 s2 0 2.5 1 L7 12"
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={sw}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                />
            </svg>
        );
    }
    if (shape === "dot") {
        return (
            <svg {...common}>
                <circle cx="12" cy="12" r="9" fill={fill} stroke={stroke} strokeWidth={sw} />
            </svg>
        );
    }
    if (shape === "crosshair") {
        return (
            <svg {...common}>
                <circle cx="12" cy="12" r="9" fill="none" stroke={fill} strokeWidth="2.5" />
                <line x1="12" y1="2"  x2="12" y2="8"  stroke={fill} strokeWidth="2.5" strokeLinecap="round" />
                <line x1="12" y1="16" x2="12" y2="22" stroke={fill} strokeWidth="2.5" strokeLinecap="round" />
                <line x1="2"  y1="12" x2="8"  y2="12" stroke={fill} strokeWidth="2.5" strokeLinecap="round" />
                <line x1="16" y1="12" x2="22" y2="12" stroke={fill} strokeWidth="2.5" strokeLinecap="round" />
                <circle cx="12" cy="12" r="1.8" fill={fill} />
            </svg>
        );
    }
    if (shape === "target") {
        return (
            <svg {...common}>
                <circle cx="12" cy="12" r="10" fill="none" stroke={fill} strokeWidth="2" />
                <circle cx="12" cy="12" r="6"  fill="none" stroke={fill} strokeWidth="2" />
                <circle cx="12" cy="12" r="2.5" fill={fill} />
            </svg>
        );
    }
    if (shape === "heart") {
        return (
            <svg {...common}>
                <path
                    d="M12 21s-7-4.5-9.5-9.2C.9 8.7 2.6 5 6 5c2 0 3.4 1.1 4 2.5C10.6 6.1 12 5 14 5c3.4 0 5.1 3.7 3.5 6.8C19 16.5 12 21 12 21z"
                    fill={fill} stroke={stroke} strokeWidth={sw} strokeLinejoin="round"
                />
            </svg>
        );
    }
    if (shape === "star") {
        return (
            <svg {...common}>
                <path
                    d="M12 2 L14.6 8.5 L21.5 9.2 L16.2 13.8 L17.8 20.5 L12 16.8 L6.2 20.5 L7.8 13.8 L2.5 9.2 L9.4 8.5 Z"
                    fill={fill} stroke={stroke} strokeWidth={sw} strokeLinejoin="round"
                />
            </svg>
        );
    }
    if (shape === "pumpkin") {
        return (
            <svg {...common}>
                <path d="M12 4 v3" stroke="#3b1e1e" strokeWidth="2" strokeLinecap="round" />
                <ellipse cx="12" cy="13" rx="9" ry="8" fill={fill} stroke={stroke} strokeWidth={sw} />
                <path d="M6 13 c0-2 0-6 2-7 M12 6 v14 M18 13 c0-2 0-6-2-7" stroke={stroke} strokeWidth="1" fill="none" opacity="0.35" />
                <polygon points="9,11 8,13 10,13" fill="#0f172a" />
                <polygon points="15,11 14,13 16,13" fill="#0f172a" />
                <path d="M8 16 L10 18 L12 16 L14 18 L16 16" stroke="#0f172a" strokeWidth="1.4" fill="none" strokeLinecap="round" />
            </svg>
        );
    }
    if (shape === "snowflake") {
        return (
            <svg {...common}>
                <g stroke={fill} strokeWidth="2" strokeLinecap="round">
                    <line x1="12" y1="2"  x2="12" y2="22" />
                    <line x1="2"  y1="12" x2="22" y2="12" />
                    <line x1="5"  y1="5"  x2="19" y2="19" />
                    <line x1="19" y1="5"  x2="5"  y2="19" />
                    <path d="M9 4 L12 6 L15 4 M9 20 L12 18 L15 20 M4 9 L6 12 L4 15 M20 9 L18 12 L20 15" fill="none" />
                </g>
                <circle cx="12" cy="12" r="1.5" fill={fill} />
            </svg>
        );
    }
    if (shape === "cake") {
        return (
            <svg {...common}>
                <path d="M12 3 v3" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="4" r="1.2" fill="#fbbf24" />
                <rect x="3" y="10" width="18" height="10" rx="2" fill={fill} stroke={stroke} strokeWidth={sw} />
                <path d="M3 14 c3 2 6-2 9 0 s6-2 9 0" stroke={stroke} strokeWidth="1.4" fill="none" />
                <rect x="3" y="18" width="18" height="3" fill={stroke} opacity="0.15" />
            </svg>
        );
    }
    if (shape === "paw") {
        return (
            <svg {...common}>
                <g fill={fill} stroke={stroke} strokeWidth={sw}>
                    <ellipse cx="6"  cy="10" rx="2.2" ry="3" />
                    <ellipse cx="18" cy="10" rx="2.2" ry="3" />
                    <ellipse cx="9"  cy="5.5" rx="1.8" ry="2.6" />
                    <ellipse cx="15" cy="5.5" rx="1.8" ry="2.6" />
                    <path d="M12 11 c-3.5 0-6 3-6 5.5 0 2 2 3.5 4 3.5 h4 c2 0 4-1.5 4-3.5 0-2.5-2.5-5.5-6-5.5z" />
                </g>
            </svg>
        );
    }
    // classic arrow
    return (
        <svg {...common}>
            <path
                d="M5 3 L5 20 L10 15 L13 22 L16 21 L13 14 L20 14 Z"
                fill={fill}
                stroke={stroke}
                strokeWidth={sw}
                strokeLinejoin="round"
            />
        </svg>
    );
};

// Back-compat alias
const CursorArrow = (props) => <CursorShape shape="arrow" {...props} />;

/* ------------------------------------------------------------------ */
/* Cursor overlay                                                      */
/* ------------------------------------------------------------------ */
const CursorRenderer = ({ enabled, color, size, trail, clickRing, shape, spotlight }) => {
    const [pos, setPos] = useState({ x: -100, y: -100 });
    const [trailDots, setTrailDots] = useState([]);
    const [rings, setRings] = useState([]);
    const trailRef = useRef([]);
    const ringIdRef = useRef(0);

    useEffect(() => {
        if (!enabled) {
            document.body.classList.remove("cc-active");
            return;
        }
        document.body.classList.add("cc-active");

        const onMove = (e) => {
            setPos({ x: e.clientX, y: e.clientY });
            if (trail) {
                const now = performance.now();
                trailRef.current = [
                    { x: e.clientX, y: e.clientY, t: now, id: now + Math.random() },
                    ...trailRef.current.filter((p) => now - p.t < 500),
                ].slice(0, 12);
                setTrailDots([...trailRef.current]);
            }
        };
        const onDown = (e) => {
            if (!clickRing) return;
            const id = ++ringIdRef.current;
            setRings((r) => [...r, { id, x: e.clientX, y: e.clientY }]);
            setTimeout(() => {
                setRings((r) => r.filter((x) => x.id !== id));
            }, 550);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mousedown", onDown);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mousedown", onDown);
            document.body.classList.remove("cc-active");
        };
    }, [enabled, trail, clickRing]);

    if (!enabled) return null;

    return (
        <div
            aria-hidden="true"
            style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9999 }}
            data-testid="custom-cursor-layer"
        >
            {/* Spotlight — Pro */}
            {spotlight && (
                <div
                    style={{
                        position: "absolute",
                        left: pos.x, top: pos.y,
                        width: size * 6, height: size * 6,
                        marginLeft: -(size * 3), marginTop: -(size * 3),
                        borderRadius: "50%",
                        boxShadow: `0 0 0 9999px rgba(15, 23, 42, 0.55)`,
                        pointerEvents: "none",
                        transition: "width 0.2s ease, height 0.2s ease",
                    }}
                    data-testid="spotlight"
                />
            )}

            {trail &&
                trailDots.map((d, i) => {
                    const age = performance.now() - d.t;
                    const alpha = Math.max(0, 1 - age / 500);
                    const s = size * (0.35 + (1 - i / trailDots.length) * 0.25);
                    return (
                        <div
                            key={d.id}
                            style={{
                                position: "absolute",
                                left: d.x, top: d.y,
                                width: s, height: s,
                                marginLeft: -s / 2, marginTop: -s / 2,
                                background: color.hex,
                                borderRadius: "50%",
                                opacity: alpha * 0.35,
                                filter: "blur(2px)",
                            }}
                        />
                    );
                })}
            {rings.map((r) => (
                <div
                    key={r.id}
                    style={{
                        position: "absolute",
                        left: r.x, top: r.y,
                        width: size * 1.8, height: size * 1.8,
                        border: `4px solid ${color.hex}`,
                        borderRadius: "50%",
                        transform: "translate(-50%, -50%) scale(0.3)",
                        animation: "cc-ring 0.55s ease-out forwards",
                    }}
                />
            ))}
            <div
                style={{ position: "absolute", left: pos.x, top: pos.y, transform: "translate(-4px, -2px)" }}
            >
                <CursorShape shape={shape} size={size} fill={color.hex} stroke={color.stroke} />
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* Toggle                                                              */
/* ------------------------------------------------------------------ */
const Toggle = ({ on, onChange, label, testid }) => (
    <button
        type="button"
        onClick={() => onChange(!on)}
        className={`toggle-wrap ${on ? "on" : ""}`}
        data-testid={testid}
        aria-pressed={on}
    >
        <span className="toggle-track"><span className="toggle-thumb" /></span>
        <span className="font-display font-bold text-lg text-slate-900">{label}</span>
    </button>
);

/* ------------------------------------------------------------------ */
/* Guides                                                              */
/* ------------------------------------------------------------------ */
const WINDOWS_STEPS = [
    'Press the Windows key on your keyboard and type "Mouse settings".',
    'Click "Mouse settings", then click "Additional mouse settings" on the right.',
    'In the pop-up, click the "Pointers" tab.',
    'Under "Scheme", choose "Windows Black (extra large)" or "Windows Inverted (extra large)".',
    'Click "Apply", then "OK". Your pointer will now be bigger and easier to see.',
    'Tip: In Settings > Accessibility > Mouse pointer, drag the size slider up and pick a bright colour.',
];
const MAC_STEPS = [
    'Click the Apple menu (top-left corner) and choose "System Settings".',
    'Click "Accessibility" in the sidebar, then click "Display".',
    'Scroll to "Pointer". Drag the "Pointer size" slider to the right.',
    'Choose a "Pointer outline colour" (black) and a bright "Pointer fill colour" (orange or green).',
    'Wiggle your mouse or trackpad and enjoy your bright, easy-to-see pointer!',
];

const AccordionItem = ({ open, onToggle, icon, title, children, testid }) => (
    <div className="acc-item" data-testid={testid}>
        <button
            type="button"
            onClick={onToggle}
            className="acc-btn"
            aria-expanded={open}
            data-testid={`${testid}-btn`}
        >
            <span className="flex items-center gap-3">{icon}{title}</span>
            <span className="text-2xl">{open ? "–" : "+"}</span>
        </button>
        {open && <div className="acc-body">{children}</div>}
    </div>
);

/* ------------------------------------------------------------------ */
/* Home                                                                */
/* ------------------------------------------------------------------ */
function Home() {
    const [colorId, setColorId] = useState(DEFAULTS.colorId);
    const [size, setSize] = useState(DEFAULTS.size);
    const [trail, setTrail] = useState(DEFAULTS.trail);
    const [clickRing, setClickRing] = useState(DEFAULTS.clickRing);
    const [enabled, setEnabled] = useState(true);
    const [shape, setShape] = useState(DEFAULTS.shape);
    const [pro, setPro] = useState(false);
    const [spotlight, setSpotlight] = useState(false);
    const [openGuide, setOpenGuide] = useState("windows");
    const [clickCount, setClickCount] = useState(0);
    const [tipLoading, setTipLoading] = useState("");
    const [tipError, setTipError] = useState("");
    const [proLoading, setProLoading] = useState(false);
    const [restoreCode, setRestoreCode] = useState("");
    const [restoreMsg, setRestoreMsg] = useState("");
    const [shareLink, setShareLink] = useState("");
    const [hasReferral, setHasReferral] = useState(false);

    // Load prefs + pro status
    useEffect(() => {
        try {
            const s = JSON.parse(localStorage.getItem("clearcursor") || "null");
            if (s) {
                setColorId(s.colorId ?? DEFAULTS.colorId);
                setSize(s.size ?? DEFAULTS.size);
                setTrail(s.trail ?? DEFAULTS.trail);
                setClickRing(s.clickRing ?? DEFAULTS.clickRing);
                setEnabled(s.enabled ?? true);
                if (s.shape) setShape(s.shape);
            }
            if (localStorage.getItem("clearcursor_pro") === "1") setPro(true);
        } catch (e) { /* ignore */ }

        // Read shareable preset from URL (?c=orange&s=80&sh=hand&t=1&r=1&ref=CODE)
        const p = new URLSearchParams(window.location.search);
        if (p.get("c")) setColorId(p.get("c"));
        if (p.get("s")) setSize(Math.min(140, Math.max(32, Number(p.get("s")) || 64)));
        if (p.get("sh")) setShape(p.get("sh"));
        if (p.get("t")) setTrail(p.get("t") === "1");
        if (p.get("r")) setClickRing(p.get("r") === "1");
        if (p.get("ref")) {
            const code = p.get("ref").trim().slice(0, 40);
            sessionStorage.setItem("clearcursor_ref", code);
        }
        // Reflect any referral (from URL or a previous visit) in state so the banner renders on first paint
        if (sessionStorage.getItem("clearcursor_ref")) setHasReferral(true);
    }, []);

    // Persist prefs
    useEffect(() => {
        localStorage.setItem(
            "clearcursor",
            JSON.stringify({ colorId, size, trail, clickRing, enabled, shape })
        );
    }, [colorId, size, trail, clickRing, enabled, shape]);

    // Spotlight hotkey — double-tap Shift
    useEffect(() => {
        if (!pro) return;
        let lastShift = 0;
        const onKey = (e) => {
            if (e.key !== "Shift") return;
            const now = Date.now();
            if (now - lastShift < 400) {
                setSpotlight((v) => !v);
                lastShift = 0;
            } else {
                lastShift = now;
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [pro]);

    const color = COLORS.find((c) => c.id === colorId) || COLORS[0];

    const resetAll = useCallback(() => {
        setColorId(DEFAULTS.colorId);
        setSize(DEFAULTS.size);
        setTrail(DEFAULTS.trail);
        setClickRing(DEFAULTS.clickRing);
        setEnabled(true);
        setShape(DEFAULTS.shape);
        setSpotlight(false);
    }, []);

    const startCheckout = async (lookup_key, isPro = false) => {
        setTipError("");
        if (isPro) setProLoading(true); else setTipLoading(lookup_key);
        try {
            const body = { lookup_key, origin_url: window.location.origin };
            const ref = sessionStorage.getItem("clearcursor_ref");
            if (ref && lookup_key === "pro_lifetime") body.ref = ref;
            const { data } = await axios.post(`${API}/payments/checkout`, body);
            window.location.href = data.checkout_url;
        } catch (e) {
            setTipError("Sorry — couldn't start checkout. Please try again in a moment.");
            setTipLoading("");
            setProLoading(false);
        }
    };

    const restorePro = async () => {
        setRestoreMsg("");
        try {
            const { data } = await axios.post(`${API}/pro/verify`, { code: restoreCode.trim() });
            if (data.pro) {
                localStorage.setItem("clearcursor_pro", "1");
                localStorage.setItem("clearcursor_pro_session", data.session_id);
                setPro(true);
                setRestoreMsg("✅ Pro unlocked on this device!");
            }
        } catch (e) {
            const msg = e?.response?.data?.detail || "That code didn't work. Double-check it or email chrisparker@gmail.com.";
            setRestoreMsg("❌ " + msg);
        }
    };

    const makeShareLink = () => {
        const p = new URLSearchParams({
            c: colorId,
            s: String(size),
            sh: shape,
            t: trail ? "1" : "0",
            r: clickRing ? "1" : "0",
        });
        const url = `${window.location.origin}/?${p.toString()}`;
        setShareLink(url);
        navigator.clipboard.writeText(url).catch(() => {});
    };

    const [refLink, setRefLink] = useState("");
    const makeReferralLink = () => {
        const code = (localStorage.getItem("clearcursor_pro_session") || "").slice(-8).toUpperCase();
        const url = `${window.location.origin}/?ref=${code || "FRIEND"}`;
        setRefLink(url);
        navigator.clipboard.writeText(url).catch(() => {});
    };

    return (
        <div className="min-h-screen grain relative">
            <CursorRenderer
                enabled={enabled} color={color} size={size}
                trail={trail} clickRing={clickRing}
                shape={shape} spotlight={spotlight && pro}
            />

            {/* HEADER */}
            <header className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-6 flex items-center justify-between relative z-10">
                <div className="flex items-center gap-3">
                    <div
                        className="w-12 h-12 rounded-2xl border-2 border-slate-900 flex items-center justify-center"
                        style={{ background: color.hex, boxShadow: "3px 3px 0 #0f172a" }}
                    >
                        <MousePointer2 size={26} color="#0f172a" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="font-display font-extrabold text-2xl md:text-3xl text-slate-900">
                            ClearCursor
                        </div>
                        <div className="text-sm md:text-base text-slate-600">
                            Find your mouse pointer. Instantly.
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <a href="#support" className="btn-pill hidden sm:inline-flex" data-testid="tip-header-cta">
                        <Coffee size={18} /> Tip
                    </a>
                    <button
                        type="button"
                        onClick={resetAll}
                        className="btn-pill hidden sm:inline-flex"
                        data-testid="reset-btn"
                    >
                        <RotateCcw size={20} /> Reset
                    </button>
                </div>
            </header>

            {/* MARQUEE */}
            <div className="border-y-2 border-slate-900 bg-slate-900 text-white py-3 relative z-10">
                <div className="marquee font-display font-bold text-xl md:text-2xl">
                    <span className="marquee-inner">
                        Bright Orange&nbsp; ✦ &nbsp;Bigger Pointer&nbsp; ✦ &nbsp;See Every
                        Click&nbsp; ✦ &nbsp;Made for Grandparents&nbsp; ✦ &nbsp;Bright
                        Green&nbsp; ✦ &nbsp;Easy on the Eyes&nbsp; ✦ &nbsp;Bright
                        Orange&nbsp; ✦ &nbsp;Bigger Pointer&nbsp; ✦ &nbsp;See Every
                        Click&nbsp; ✦ &nbsp;Made for Grandparents&nbsp; ✦ &nbsp;
                    </span>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Hero */}
                    <section className="lg:col-span-7 card-bento" style={{ background: "#FFF7E0" }}>
                        {hasReferral && !pro && (
                            <div
                                className="mb-4 -mx-2 px-4 py-3 rounded-2xl border-2 border-slate-900 flex items-center gap-3"
                                style={{ background: "#FF5722", color: "#fff", boxShadow: "3px 3px 0 #0f172a" }}
                                data-testid="referral-banner"
                            >
                                <Crown size={22} />
                                <div className="font-display font-bold text-base md:text-lg">
                                    A friend sent you here — Pro is 50% off ($2.49) just for you.
                                </div>
                            </div>
                        )}
                        <span className="tag">Accessibility · v1</span>
                        <h1 className="font-display font-extrabold text-4xl md:text-6xl leading-[1.05] text-slate-900 mt-4">
                            Never lose your <span style={{ color: color.hex }}>mouse&nbsp;pointer</span> again.
                        </h1>
                        <p className="text-lg md:text-xl text-slate-800 mt-5 max-w-2xl">
                            Pick a colour. Make it big. Watch it glow every time you move
                            or click. Perfect for parents, grandparents, and anyone who
                            wants a mouse pointer they can actually see.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <a href="#playground" className="btn-pill solid" data-testid="try-cta">
                                <Hand size={20} /> Try it below
                            </a>
                            <a href="#guide" className="btn-pill" data-testid="guide-cta">
                                <Monitor size={20} /> Change my system cursor
                            </a>
                        </div>
                    </section>

                    {/* Live preview */}
                    <section
                        className="lg:col-span-5 card-bento flex flex-col items-center justify-center"
                        style={{ background: color.hex + "22" }}
                        data-testid="live-preview"
                    >
                        <span className="tag" style={{ background: "#fff" }}>Live preview</span>
                        <div className="my-6" style={{ transform: `scale(${size / 32})` }}>
                            <CursorShape shape={shape} size={64} fill={color.hex} stroke={color.stroke} />
                        </div>
                        <div className="font-display font-bold text-xl text-slate-900">
                            {color.label} · {size}px
                        </div>
                        <div className="text-slate-700 text-base mt-1">
                            {enabled ? "Active on this page" : "Currently paused"}
                        </div>
                    </section>

                    {/* Colour */}
                    <section className="lg:col-span-7 card-bento">
                        <div className="flex items-center gap-3 mb-5">
                            <Palette size={26} />
                            <h2 className="font-display font-extrabold text-2xl md:text-3xl">Pick your colour</h2>
                        </div>
                        <div className="flex flex-wrap gap-5">
                            {COLORS.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => setColorId(c.id)}
                                    className={`swatch ${colorId === c.id ? "active" : ""}`}
                                    style={{ background: c.hex }}
                                    aria-label={c.label}
                                    data-testid={`swatch-${c.id}`}
                                >
                                    {colorId === c.id && (
                                        <Check size={30} strokeWidth={3.5} color="#0f172a" />
                                    )}
                                </button>
                            ))}
                        </div>
                        <p className="text-slate-700 mt-4 text-base">
                            Bright orange and bright green stand out best against most
                            websites — they're our top picks.
                        </p>
                    </section>

                    {/* Size */}
                    <section className="lg:col-span-5 card-bento">
                        <div className="flex items-center gap-3 mb-5">
                            <Ruler size={26} />
                            <h2 className="font-display font-extrabold text-2xl md:text-3xl">Choose a size</h2>
                        </div>
                        <div className="flex items-end gap-4">
                            <div className="font-display font-extrabold text-6xl leading-none">{size}</div>
                            <div className="text-slate-700 pb-2">pixels</div>
                        </div>
                        <input
                            type="range" min="32" max="140" step="2"
                            value={size}
                            onChange={(e) => setSize(Number(e.target.value))}
                            className="big-slider mt-5"
                            data-testid="size-slider"
                            aria-label="Cursor size"
                        />
                        <div className="flex justify-between text-sm text-slate-700 mt-2 font-medium">
                            <span>Small</span><span>Regular</span><span>Huge</span>
                        </div>
                    </section>

                    {/* Extras */}
                    <section className="lg:col-span-7 card-bento">
                        <div className="flex items-center gap-3 mb-5">
                            <Sparkles size={26} />
                            <h2 className="font-display font-extrabold text-2xl md:text-3xl">
                                Extra visibility helpers
                            </h2>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-6 sm:gap-10">
                            <Toggle on={trail} onChange={setTrail} label="Show cursor trail" testid="toggle-trail" />
                            <Toggle on={clickRing} onChange={setClickRing} label="Show click ring" testid="toggle-ring" />
                            <Toggle on={enabled} onChange={setEnabled} label="Bright cursor on" testid="toggle-enabled" />
                        </div>
                        <p className="text-slate-700 mt-5 text-base">
                            The trail leaves a soft coloured tail behind the pointer. The
                            click ring flashes a coloured circle every time you click —
                            great for teaching a new user where they just clicked.
                        </p>
                    </section>

                    {/* Pro features (shape picker, spotlight, share) */}
                    <section className="lg:col-span-12 card-bento" data-testid="pro-features"
                        style={{ background: pro ? "linear-gradient(135deg, #FFF7E0 0%, #FFE9D6 100%)" : "#fff" }}>
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                            <div className="flex items-center gap-3">
                                <Crown size={26} color={pro ? "#FF5722" : "#0f172a"} />
                                <h2 className="font-display font-extrabold text-2xl md:text-3xl">
                                    Pro features
                                </h2>
                                {pro ? (
                                    <span className="tag" style={{ background: "#FF5722", color: "#fff", borderColor: "#0f172a" }}>
                                        Unlocked
                                    </span>
                                ) : (
                                    <span className="tag">Locked · $4.99</span>
                                )}
                            </div>
                            {!pro && (
                                <button
                                    type="button"
                                    onClick={() => startCheckout("pro_lifetime", true)}
                                    disabled={proLoading}
                                    className="btn-pill solid disabled:opacity-60"
                                    data-testid="unlock-pro-btn"
                                >
                                    <Crown size={18} />
                                    {proLoading ? "Opening…" : "Unlock Pro — $4.99"}
                                </button>
                            )}
                        </div>

                        {/* Shape picker */}
                        <div className="mb-6">
                            <div className="font-display font-bold text-lg text-slate-900 mb-3">
                                Cursor shape
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {SHAPES.map((s) => {
                                    const locked = s.pro && !pro;
                                    const active = shape === s.id;
                                    return (
                                        <button
                                            key={s.id}
                                            type="button"
                                            disabled={locked}
                                            onClick={() => setShape(s.id)}
                                            className="card-bento flex flex-col items-center justify-center disabled:opacity-50"
                                            style={{
                                                padding: "14px",
                                                width: 110,
                                                background: active ? color.hex + "33" : "#fff",
                                                outline: active ? "3px solid #0f172a" : "none",
                                                outlineOffset: 3,
                                            }}
                                            data-testid={`shape-${s.id}`}
                                        >
                                            <CursorShape shape={s.id} size={40} fill={color.hex} stroke={color.stroke} />
                                            <div className="text-sm font-medium mt-2 text-slate-800">
                                                {s.label}
                                            </div>
                                            {locked && (
                                                <div className="text-xs text-slate-600 flex items-center gap-1 mt-1">
                                                    <Lock size={12} /> Pro
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Spotlight + share */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <div className="flex items-start gap-4">
                                <div className="mt-1"><Search size={26} color={pro ? "#0f172a" : "#94a3b8"} /></div>
                                <div className="flex-1">
                                    <div className="font-display font-bold text-lg text-slate-900">
                                        Where-is-my-cursor spotlight
                                    </div>
                                    <p className="text-slate-700 mt-1 text-base">
                                        Double-tap <kbd className="px-2 py-1 border-2 border-slate-900 rounded font-mono text-sm">Shift</kbd> to
                                        dim the screen around your pointer. Perfect for finding a lost cursor instantly.
                                    </p>
                                    <div className="mt-3">
                                        {pro ? (
                                            <button
                                                type="button"
                                                onClick={() => setSpotlight((v) => !v)}
                                                className={`btn-pill ${spotlight ? "solid" : ""}`}
                                                data-testid="spotlight-toggle"
                                            >
                                                <Search size={18} />
                                                {spotlight ? "Spotlight ON" : "Try it now"}
                                            </button>
                                        ) : (
                                            <span className="text-slate-600 flex items-center gap-2">
                                                <Lock size={16} /> Requires Pro
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="mt-1"><Share2 size={26} color={pro ? "#0f172a" : "#94a3b8"} /></div>
                                <div className="flex-1">
                                    <div className="font-display font-bold text-lg text-slate-900">
                                        Shareable preset link
                                    </div>
                                    <p className="text-slate-700 mt-1 text-base">
                                        Copy a link that pre-loads your exact colour, shape, and size —
                                        perfect for setting up a parent or grandparent's laptop.
                                    </p>
                                    <div className="mt-3">
                                        {pro ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={makeShareLink}
                                                    className="btn-pill"
                                                    data-testid="share-btn"
                                                >
                                                    <Share2 size={18} /> {shareLink ? "Copied!" : "Copy my preset link"}
                                                </button>
                                                {shareLink && (
                                                    <div className="mt-2 text-xs text-slate-600 break-all">
                                                        {shareLink}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-slate-600 flex items-center gap-2">
                                                <Lock size={16} /> Requires Pro
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!pro && (
                            <div className="mt-8 pt-6 border-t-2 border-dashed border-slate-400">
                                <div className="flex items-center gap-2 mb-2 text-slate-800">
                                    <KeyRound size={18} />
                                    <span className="font-display font-bold">Already paid? Restore Pro</span>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <input
                                        type="text"
                                        value={restoreCode}
                                        onChange={(e) => setRestoreCode(e.target.value)}
                                        placeholder="Paste your 12-char unlock code"
                                        className="flex-1 px-4 py-3 border-2 border-slate-900 rounded-full font-mono text-base"
                                        data-testid="restore-code-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={restorePro}
                                        className="btn-pill"
                                        data-testid="restore-btn"
                                    >
                                        <KeyRound size={16} /> Restore
                                    </button>
                                </div>
                                {restoreMsg && (
                                    <p className="mt-3 text-base font-medium" data-testid="restore-msg">
                                        {restoreMsg}
                                    </p>
                                )}
                            </div>
                        )}

                        {pro && (
                            <div className="mt-8 pt-6 border-t-2 border-dashed border-slate-400" data-testid="referral-section">
                                <div className="flex items-center gap-2 mb-2 text-slate-800">
                                    <Share2 size={18} />
                                    <span className="font-display font-bold">
                                        Refer a friend — they get Pro for 50% off
                                    </span>
                                </div>
                                <p className="text-slate-700 text-base mb-3 max-w-2xl">
                                    Share your personal link. Anyone who unlocks Pro through it
                                    pays just <strong>$2.49</strong> instead of $4.99. Great for
                                    setting up your own family too.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        type="button"
                                        onClick={makeReferralLink}
                                        className="btn-pill solid"
                                        data-testid="referral-btn"
                                    >
                                        <Share2 size={18} />
                                        {refLink ? "Copied!" : "Copy my 50%-off link"}
                                    </button>
                                    {refLink && (
                                        <div className="flex-1 text-sm text-slate-700 break-all bg-white/70 border-2 border-slate-900 rounded-full px-4 py-3 font-mono" data-testid="referral-link">
                                            {refLink}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </section>

                    {/* Playground */}
                    <section
                        id="playground"
                        className="lg:col-span-5 card-bento play-target"
                        data-testid="playground"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <Zap size={26} />
                            <h2 className="font-display font-extrabold text-2xl">Practice area</h2>
                        </div>
                        <p className="text-slate-800 text-base mb-5">
                            Move your mouse around and click anywhere inside this box to
                            test your new cursor.
                        </p>
                        <button
                            type="button"
                            onClick={() => setClickCount((c) => c + 1)}
                            className="btn-pill solid w-full justify-center text-xl"
                            style={{ background: color.hex, color: "#0f172a", borderColor: "#0f172a" }}
                            data-testid="playground-btn"
                        >
                            <Hand size={22} /> Click me — clicked {clickCount} times
                        </button>
                    </section>

                    {/* Guide */}
                    <section id="guide" className="lg:col-span-12 card-bento">
                        <div className="flex items-center gap-3 mb-2">
                            <Eye size={26} />
                            <h2 className="font-display font-extrabold text-3xl md:text-4xl">
                                Make your <em className="not-italic underline decoration-4" style={{ textDecorationColor: color.hex }}>real</em> cursor bigger too
                            </h2>
                        </div>
                        <p className="text-slate-700 text-base md:text-lg mb-6 max-w-3xl">
                            The bright cursor above works while you visit this website. To
                            make your normal mouse pointer bigger on every app and window,
                            follow the simple steps for your computer below.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <AccordionItem
                                testid="guide-windows"
                                open={openGuide === "windows"}
                                onToggle={() => setOpenGuide(openGuide === "windows" ? "" : "windows")}
                                icon={<Monitor size={26} />}
                                title="On Windows 10 / 11"
                            >
                                <ol className="list-decimal pl-6 space-y-3">
                                    {WINDOWS_STEPS.map((s, i) => <li key={i}>{s}</li>)}
                                </ol>
                            </AccordionItem>
                            <AccordionItem
                                testid="guide-mac"
                                open={openGuide === "mac"}
                                onToggle={() => setOpenGuide(openGuide === "mac" ? "" : "mac")}
                                icon={<Apple size={26} />}
                                title="On Mac (macOS Ventura or newer)"
                            >
                                <ol className="list-decimal pl-6 space-y-3">
                                    {MAC_STEPS.map((s, i) => <li key={i}>{s}</li>)}
                                </ol>
                            </AccordionItem>
                        </div>
                    </section>

                    {/* SUPPORT / TIP JAR */}
                    <section
                        id="support"
                        className="lg:col-span-12 card-bento"
                        style={{ background: "linear-gradient(135deg, #FFF7E0 0%, #FFE9D6 100%)" }}
                        data-testid="support-section"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <Coffee size={26} />
                            <span className="tag" style={{ background: "#fff" }}>Support the project</span>
                        </div>
                        <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900">
                            ClearCursor is free — always.
                        </h2>
                        <p className="text-lg md:text-xl text-slate-800 mt-3 max-w-3xl">
                            If it helped you or someone in your family, consider leaving a
                            small tip. It pays for the domain, hosting, and lets me build
                            more accessibility tools like this.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-7">
                            {TIP_TIERS.map((t) => (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => startCheckout(t.key)}
                                    disabled={!!tipLoading}
                                    className="card-bento text-left disabled:opacity-60"
                                    style={{ background: "#fff", padding: "22px", cursor: "pointer" }}
                                    data-testid={`tip-btn-${t.key}`}
                                >
                                    <div className="text-slate-700 font-medium text-base">{t.label}</div>
                                    <div className="font-display font-extrabold text-4xl mt-1 text-slate-900">
                                        {t.amount}
                                    </div>
                                    <div className="text-sm text-slate-600 mt-1">{t.hint}</div>
                                    <div className="mt-4 btn-pill solid inline-flex text-base">
                                        <Coffee size={18} />
                                        {tipLoading === t.key ? "Opening…" : `Tip ${t.amount}`}
                                    </div>
                                </button>
                            ))}
                        </div>
                        {tipError && (
                            <p className="mt-4 text-red-700 font-medium" data-testid="tip-error">
                                {tipError}
                            </p>
                        )}
                        <p className="text-sm text-slate-700 mt-5 flex items-center gap-2">
                            <ShieldCheck size={16} /> Secure checkout by Stripe. No account needed.
                        </p>
                    </section>
                </div>

                {/* Footer */}
                <footer className="mt-14 pt-8 border-t-2 border-slate-900">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                        <div>
                            <div className="font-display font-extrabold text-xl text-slate-900">
                                ClearCursor
                            </div>
                            <p className="text-slate-700 text-base mt-2">
                                Built with love for the folks who've been searching the
                                screen for that little arrow for far too long.
                            </p>
                        </div>
                        <div>
                            <div className="font-display font-bold text-lg text-slate-900 mb-2">
                                Explore
                            </div>
                            <ul className="space-y-1 text-slate-800">
                                <li>
                                    <Link to="/thanks" className="underline hover:text-slate-900" data-testid="footer-thanks">
                                        Hall of Thanks
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/print-guide" className="underline hover:text-slate-900" data-testid="footer-print">
                                        Printable setup guide
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/terms" className="underline hover:text-slate-900" data-testid="footer-terms">
                                        Terms of Service
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/privacy" className="underline hover:text-slate-900" data-testid="footer-privacy">
                                        Privacy Policy
                                    </Link>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <div className="font-display font-bold text-lg text-slate-900 mb-2">
                                Contact
                            </div>
                            <a
                                href="mailto:chrisparker@gmail.com"
                                className="inline-flex items-center gap-2 underline text-slate-800 hover:text-slate-900"
                                data-testid="footer-email"
                            >
                                <Mail size={18} /> chrisparker@gmail.com
                            </a>
                        </div>
                    </div>
                    <div className="mt-8 pt-6 border-t border-slate-300 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-sm text-slate-700">
                        <div data-testid="copyright">
                            © 2026 Christopher T. Parker. All rights reserved. ClearCursor™.
                        </div>
                        <button
                            type="button"
                            onClick={resetAll}
                            className="btn-pill text-sm"
                            data-testid="reset-btn-footer"
                        >
                            <RotateCcw size={16} /> Reset to defaults
                        </button>
                    </div>
                </footer>
            </main>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Root                                                                */
/* ------------------------------------------------------------------ */
export default function App() {
    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/thanks" element={<ThanksPage />} />
                <Route path="/print-guide" element={<PrintGuidePage />} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            </Routes>
        </BrowserRouter>
    );
}
